/**
 * Per-screen anchors for the context-aware Help panel: which screen the user
 * is on (matched by route prefix, most specific first), what the page is in
 * one breath, and what you can / can't do there. Articles bind to these keys
 * via their frontmatter `screens:` list.
 */
export const HELP_PAGES = [
  {
    key: 'needs-you',
    match: ['/needs-you'],
    title: 'Needs you',
    blurb: 'The questions only you can answer, each with its answer on the card, and everything Kodahfy did for you, with undo.',
    can: [
      'Answer a question with one press — the button does the work',
      'Undo anything Kodahfy did, day by day, under Done for you',
      'Match the rest by hand, under Match by hand'
    ],
    cant: [
      'See coaching notes — those live in the Sunday letter and the Coach'
    ]
  },
  {
    key: 'reconciliation',
    match: ['/reconciliation'],
    title: 'Reconciliation',
    blurb: 'Where transactions become trades. Your P&L, NAV and win rate are all built here.',
    can: [
      'Confirm a buy into an open trade — direction, system, stop',
      'Link a sell to the position it closed',
      'Approve dividends, costs and deposits on the Portfolio tab',
      'Send the whole backlog to Auto-Match instead'
    ],
    cant: [
      "Delete transactions — that's Transactions → Import History",
      'Change which columns you see — Column Settings, top right'
    ]
  },
  {
    key: 'trades',
    match: ['/trades', '/recode'],
    title: 'Trades',
    blurb: 'Every reconciled trade — open and closed — with R-multiples, filters and bulk tools.',
    can: [
      'Filter, sort and expand any trade',
      'Edit a trade, or delete one back to the queue (Remove & Redo)',
      'Re-file trades in bulk with Find & Recode',
      'Pick your columns and save the layout as a view'
    ],
    cant: [
      'Create a trade from nothing here — trades come from Reconciliation'
    ]
  },
  {
    key: 'transactions',
    match: ['/transactions'],
    title: 'Transactions',
    blurb: 'The raw ledger from your brokers — every buy, sell, dividend, cost and deposit, plus Import.',
    can: [
      'Drop a broker file on Import — nothing saves until you continue past the summary',
      'See every imported row and its reconciliation status',
      'Delete a whole import batch from Import History'
    ],
    cant: []
  },
  {
    key: 'nav',
    match: ['/nav', '/portfolio-nav'],
    title: 'Portfolio NAV',
    blurb: 'Your account measured like a fund: NAV, units, and the full monthly money story.',
    can: [
      'Read NAV per unit — the line deposits cannot fake',
      'Walk any month’s line items: P&L, dividends, costs, capital',
      'See year-to-date return and maximum drawdown'
    ],
    cant: []
  },
  {
    key: 'automation',
    match: ['/settings?tab=automation'],
    title: 'Automation',
    blurb: 'What Kodahfy does for you without being asked — Auto-Match, IB sync, portfolio rules.',
    can: [
      'Switch Auto-Match on and set your default system',
      'Add per-account rules that override the default',
      'Preview, apply and undo an Auto-Match run'
    ],
    cant: []
  },
  {
    key: 'settings',
    match: ['/settings'],
    title: 'Settings',
    blurb: 'Accounts, trade settings, automation, FX, profile — everything configurable lives here.',
    can: [],
    cant: []
  },
  {
    key: 'mind-markets',
    match: ['/mind-markets'],
    title: 'Mind & Markets',
    blurb: 'The human side of the ledger — journal entries and psychology check-ins, beside your trading data.',
    can: [
      'Write a journal entry with mindset and discipline ratings',
      'Complete daily, weekly and monthly psychology check-ins'
    ],
    cant: []
  },
  {
    key: 'trading-plan',
    match: ['/trading-plan'],
    title: 'Trading Plan',
    blurb: 'Your written rules, in versions — and the Coach reads them, so you can be held to them.',
    can: [
      'Create a plan from a template',
      'Save new versions as your rules evolve'
    ],
    cant: []
  },
  {
    key: 'macro',
    match: ['/macro', '/charts'],
    title: 'Macro',
    blurb: 'The market, top down: what conditions are like, where the money is going, which sectors lead. Updated overnight.',
    can: [
      'Read the market conditions state, its score and what came after it in the past',
      'Drag the window on the timeline to see any period since 2019',
      'See which asset classes beat cash on the momentum ladder',
      'Ask the coach how the tape sits against what you hold'
    ],
    cant: [
      "Get a forecast — it reports what the tape did, it doesn't predict",
      'Chart a single stock — use your broker or charting platform for that'
    ]
  },
  {
    key: 'notes',
    match: ['/notes'],
    title: 'Notes',
    blurb: 'Your research in notebooks: course notes, system ideas, market reading. Pictures, search, and a coach that can read them.',
    can: [
      'Keep notes in notebooks and move them between them',
      'Paste or drag in charts, and resize them',
      'Search every note at once',
      'Ask the coach what your notes say'
    ],
    cant: [
      'Share a note with someone else — notes are private to you',
      'Recover a deleted note yourself — ask us and we can bring it back'
    ]
  },
  {
    key: 'feedback',
    match: ['/feedback'],
    title: 'Feedback',
    blurb: 'Suggest features other users can upvote, or report a bug straight to the team.',
    can: [
      'Post a feature idea to the public board (anonymously)',
      'Report a bug privately, with screenshots'
    ],
    cant: []
  },
  {
    key: 'dashboard',
    match: ['/dashboard', '/'],
    title: 'Dashboard',
    blurb: 'The at-a-glance read on your trading: P&L, R, risk, and the equity curve.',
    can: [
      'Click any chart bar to see the trades behind it',
      'Filter by system or period'
    ],
    cant: []
  }
];

export const pageForPath = (pathname, search = '') => {
  const full = pathname + (search || '');
  // most specific first: query-string matches, then longest path prefix
  const withQuery = HELP_PAGES.filter((p) => p.match.some((m) => m.includes('?') && full.startsWith(m.split('?')[0]) && full.includes(m.split('?')[1])));
  if (withQuery.length) return withQuery[0];
  const candidates = HELP_PAGES
    .map((p) => ({ p, len: Math.max(...p.match.filter((m) => !m.includes('?') && pathname.startsWith(m) && (m !== '/' || pathname === '/')).map((m) => m.length), -1) }))
    .filter((x) => x.len >= 0)
    .sort((a, b) => b.len - a.len);
  return candidates.length ? candidates[0].p : null;
};
